import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";
import { db, storage } from "@/firebase";
import { DraftTemplate, Template } from "@/types/templates";
import { RootState } from "@/store/store";
import { getDownloadURL, listAll, ref, uploadBytes } from "firebase/storage";
import { produce } from "immer";
import { get, pick } from "lodash";

type TemplatesState = Record<Template["id"], Template & { images?: string[] }>;

const initialState: TemplatesState = {};

export const fetchTemplates = createAsyncThunk("templates/fetchTemplates", async () => {
  try {
    const collectionRef = collection(db, "templates");
    const templates = await getDocs(collectionRef).then((querySnapshot) => {
      return querySnapshot.docs.reduce((acc, doc) => {
        return { ...acc, [doc.id]: { ...doc.data(), id: doc.id } };
      }, {});
    });
    return templates as Template[];
  } catch (error) {
    console.error(error);
  }
});

export const fetchTemplateById = createAsyncThunk("templates/fetchTemplateById", async (templateId: Template["id"]) => {
  try {
    const docRef = doc(db, "templates", templateId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) return;
    return {
      id: templateId,
      ...docSnap.data(),
    } as Template;
  } catch (error) {
    console.error(error);
  }
});

export const addTemplate = createAsyncThunk("templates/addTemplate", async (template: DraftTemplate, { getState }) => {
  const { auth } = getState() as RootState;
  try {
    const collectionRef = collection(db, "templates");
    const doc = await addDoc(collectionRef, template);
    return {
      ...template,
      id: doc.id,
      createdBy: auth.user.uid,
      createdAt: Date.now(),
    };
  } catch (error) {
    console.error(error);
  }
});

export const updateTemplate = createAsyncThunk("templates/updateTemplate", async (template: Template, { getState }) => {
  const { auth } = getState() as RootState;

  try {
    const docRef = doc(db, `templates/${template.id}`);

    const ALLOWED_FIELDS = ["title", "isPublished", "isPrivate", "type", "structure", "data", "styles"];

    await updateDoc(docRef, {
      ...pick(template, ALLOWED_FIELDS),
      lastUpdatedBy: auth.user.uid,
      lastUpdatedAt: Date.now(),
    });

    return template;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

interface TemplateStylesPayload {
  template: Template;
  styles: Template["styles"];
}

export const updateTemplateStyles = createAsyncThunk("templates/updateTemplateStyles", async ({ template, styles }: TemplateStylesPayload) => {
  try {
    if (!template) throw Error("No template available");

    const newTemplates = {
      ...template,
      styles,
    };

    const docRef = doc(db, `templates/${template.id}`);
    await updateDoc(docRef, newTemplates);

    return newTemplates;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteTemplate = createAsyncThunk("templates/deleteTemplate", async (id: Template["id"]) => {
  const docRef = doc(db, `templates/${id}`);
  await deleteDoc(docRef);
  return id;
});

interface FetchTemplateImages {
  templateId: string;
}

// ✅ Checked
export const fetchTemplateImages = createAsyncThunk("templates/fetchTemplateImages", async ({ templateId }: FetchTemplateImages) => {
  if (!templateId) return;

  try {
    const folderRef = ref(storage, `images/templates/${templateId}`);
    const images = [];
    const response = await listAll(folderRef);

    for await (const item of Object.values(response.items)) {
      const downloadURL = await getDownloadURL(item);
      images.push(downloadURL);
    }

    return { templateId, images };
  } catch (error) {
    console.error(error);
  }
});

interface ImageUpload {
  templateId: Template["id"];
  file: File;
}

// ✅ checked
export const uploadTemplateImage = createAsyncThunk("organization/uploadTemplateImage", async ({ templateId, file }: ImageUpload) => {
  if (!file || !templateId) return;

  try {
    const fileRef = ref(storage, `images/templates/${templateId}/${file.name}`);
    const snapshot = await uploadBytes(fileRef, file);
    const image = await getDownloadURL(snapshot.ref);

    return { templateId, image };
  } catch (error) {
    console.error(error);
  }
});

const templateSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {},
  extraReducers: (builder: any) => {
    // ✅ checked
    builder.addCase(uploadTemplateImage.fulfilled, (state, action: PayloadAction<{ templateId: string; image: string } | undefined>) => {
      if (!action.payload) return state;
      const { templateId, image } = action.payload;

      const nextState = produce(state, (draft) => {
        if (draft[templateId]?.images) {
          draft[templateId].images.push(image);
        }
        return draft;
      });

      return nextState;
    });
    builder.addCase(fetchTemplates.fulfilled, (state: unknown, action: { payload: unknown }) => {
      if (action.payload) {
        return action.payload;
      }
      return state;
    });
    builder.addCase(fetchTemplateById.fulfilled, (state: TemplatesState, action: { payload: Template }) => {
      if (action.payload) {
        return {
          ...state,
          [action.payload.id]: action.payload,
        };
      }
      return state;
    });
    builder.addCase(addTemplate.fulfilled, (state: any, action: any) => {
      if (!action.payload) return;

      return {
        ...state,
        [action.payload.id]: action.payload,
      };
    });
    builder.addCase(updateTemplate.fulfilled, (state: any, action: any) => {
      if (action.payload) {
        const { id, ...rest } = action.payload;

        state[id] = {
          ...state[id],
          ...rest,
        };
      }
    });

    builder.addCase(updateTemplateStyles.fulfilled, (state: any, action: any) => {
      if (action.payload) {
        const { id, ...rest } = action.payload;

        state[id] = {
          ...state[id],
          ...rest,
        };
      }
    });
    builder.addCase(deleteTemplate.fulfilled, (state: any, action: any) => {
      if (action.payload) {
        delete state[action.payload];
        return state;
      }
    });
    builder.addCase(fetchTemplateImages.fulfilled, (state, action: PayloadAction<{ templateId: string; images: string[] } | undefined>) => {
      if (!action.payload) return state;
      const { templateId, images } = action.payload;

      return {
        ...state,
        [templateId]: {
          ...state[templateId],
          images: images,
        },
      };
    });
  },
});

// TODO: move this to the global types file
interface Store {
  templates: {
    [id: string]: Template;
  };
}

export const templatesSelector = (state: Store) => state.templates;

export const templateListSelector = createSelector(templatesSelector, (templates) => {
  return Object.entries(templates).map(([id, template]) => ({
    ...template,
    id,
  }));
});

export const imagesByTemplateId = (templateId?: string) => {
  return createSelector(templatesSelector, (templates) => {
    return get(templates, `${templateId}.images`, []);
  });
};

export const templateById = (templateId?: string) => createSelector(templatesSelector, (templates) => get(templates, `templates.${templateId}`, null));

export default templateSlice.reducer;
